import { ApplicationController, observe, unobserve } from '@aha-app/mvc';
import WidgetsController from '../../../controllers/WidgetsController';
import type TimerWidget from '../../../widgets/TimerWidget';
import { DEFAULT_DURATION, formattedTime } from '../../../utils/time';

interface State {
  widget?: TimerWidget;
  remainingDurationText: string;
  running: boolean;
}

interface ControllerProps {
  widget: TimerWidget;
}

class TimerWidgetController extends ApplicationController<
  State,
  ControllerProps
> {
  widgetsController: WidgetsController;
  widgetObserver: ReturnType<typeof observe>;
  widgetListener: () => void;

  static initialState: State = {
    remainingDurationText: formattedTime(DEFAULT_DURATION),
    running: false,
  };

  async initialize({ widget }): Promise<void> {
    this.widgetsController = this.findControllerInstance(WidgetsController)!;
    this.state.widget = widget;

    this.widgetListener = () => this.actionStopWidget(true);

    document.addEventListener(
      'aha.whiteboard.timerStoppedSidePanel',
      this.widgetListener
    );

    this.widgetObserver = observe(() => {
      const widget = this.state.widget;
      const active = widget?.active();
      const remainingDuration = widget?.remainingDuration();
      const expired = remainingDuration === 0;

      if (!this.state.running && active && !expired) {
        this.state.running = true;
      }

      if (remainingDuration) {
        const timeText = formattedTime(remainingDuration);

        if (this.state.remainingDurationText !== timeText)
          this.state.remainingDurationText = timeText;
      }
    });
  }

  destroy(): void {
    document.removeEventListener(
      'aha.whiteboard.timerStoppedSidePanel',
      this.widgetListener
    );

    unobserve(this.widgetObserver);
  }

  visible(): boolean {
    return this.state.running;
  }

  actionStopWidget(fromSidePanel: boolean = false): void {
    if (!this.state.running) return;

    // Signal to the side panel to stop showing the 'expired' UX
    if (!fromSidePanel) {
      document.dispatchEvent(
        new CustomEvent('aha.whiteboard.timerStoppedWidget')
      );
    }

    this.state.running = false;
  }

  actionUpdateRemainingDuration(): void {
    if (!this.state.widget) return;

    const duration = this.state.widget.remainingDuration();
    this.state.remainingDurationText = formattedTime(duration);
  }
}

export default TimerWidgetController;
